import { useState, useEffect, useRef } from 'react';

const LANGUAGES = [
  { code: 'en', label: 'English', native: 'English' },
  { code: 'hi', label: 'Hindi', native: 'हिन्दी' },
  { code: 'gu', label: 'Gujarati', native: 'ગુજરાતી' },
  { code: 'mr', label: 'Marathi', native: 'मराठी' },
  { code: 'ta', label: 'Tamil', native: 'தமிழ்' },
  { code: 'te', label: 'Telugu', native: 'తెలుగు' },
  { code: 'bn', label: 'Bengali', native: 'বাংলা' },
  { code: 'pa', label: 'Punjabi', native: 'ਪੰਜਾਬੀ' },
  { code: 'es', label: 'Spanish', native: 'Español' },
  { code: 'fr', label: 'French', native: 'Français' },
  { code: 'de', label: 'German', native: 'Deutsch' },
  { code: 'ar', label: 'Arabic', native: 'العربية' },
  { code: 'zh-CN', label: 'Chinese', native: '中文' },
];

export default function WhatsAppButton() {
  const [showTranslate, setShowTranslate] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get current language from cookie
  const getCurrentLang = () => {
    const match = document.cookie.match(/googtrans=\/en\/([^;]+)/);
    return match ? match[1] : 'en';
  };

  const [selectedLang, setSelectedLang] = useState(
    LANGUAGES.find(l => l.code === getCurrentLang()) || LANGUAGES[0]
  );

  useEffect(() => {
    // Hide Google Translate standard UI elements
    const style = document.createElement('style');
    style.innerHTML = `
      .goog-te-banner-frame.skiptranslate, .goog-te-gadget-icon { display: none !important; }
      body { top: 0px !important; }
      .goog-tooltip { display: none !important; }
      .goog-tooltip:hover { display: none !important; }
      .goog-text-highlight { background-color: transparent !important; border: none !important; box-shadow: none !important; }
      #google_translate_element { display: none !important; }
      .VIpgJd-Zvi9nd-ORHb-nSjgWb { display: none !important; }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowTranslate(false);
      }
    };
    if (showTranslate) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTranslate]);

  const applyLanguage = (lang: typeof LANGUAGES[0]) => {
    setSelectedLang(lang);
    setIsTranslating(true);
    setShowTranslate(false);

    // 1. Set the cookie for the selected language
    // Format: /pageLanguage/targetLanguage
    const cookieValue = `/en/${lang.code}`;
    const domain = window.location.hostname;
    
    // Set cookie for current domain and all subdomains
    document.cookie = `googtrans=${cookieValue}; path=/; expires=Fri, 31 Dec 9999 23:59:59 GMT`;
    document.cookie = `googtrans=${cookieValue}; domain=.${domain}; path=/; expires=Fri, 31 Dec 9999 23:59:59 GMT`;
    
    // 2. If it's English, we might want to clear the cookie to revert
    if (lang.code === 'en') {
      document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=.${domain}; path=/;`;
    }

    // 3. Reload the page to apply the translation sitewide
    // This is the most reliable way to trigger Google Translate on a dynamic React site
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  return (
    <>
      {/* Hidden Google Translate Target */}
      <div id="google_translate_element" style={{ display: 'none' }} />

      {/* Language Translator */}
      <div ref={dropdownRef} className="fixed bottom-6 left-6 z-[60]">
        {showTranslate && (
          <div className="absolute bottom-16 left-0 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden w-52 animate-fade-in">
            <div className="bg-green-600 px-4 py-2.5">
              <p className="text-xs font-bold text-white tracking-wide uppercase">Select Language</p>
            </div>
            <ul className="max-h-72 overflow-y-auto py-1">
              {LANGUAGES.map((lang) => (
                <li key={lang.code}>
                  <button
                    onClick={() => applyLanguage(lang)}
                    className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors cursor-pointer hover:bg-green-50 ${
                      selectedLang.code === lang.code
                        ? 'bg-green-50 text-green-700 font-semibold'
                        : 'text-gray-700'
                    }`}
                  >
                    <span>{lang.label}</span>
                    <span className="text-xs text-gray-400">{lang.native}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <button
          onClick={() => setShowTranslate(!showTranslate)}
          className={`w-14 h-14 rounded-full flex items-center justify-center shadow-2xl transition-all transform hover:scale-110 cursor-pointer border-2 ${
            showTranslate
              ? 'bg-green-600 border-green-600'
              : 'bg-white border-green-500 hover:bg-green-50'
          }`}
          aria-label="Translate Page"
        >
          {isTranslating ? (
            <i className="ri-loader-4-line text-2xl text-green-600 animate-spin"></i>
          ) : (
            <i className={`ri-global-line text-2xl ${showTranslate ? 'text-white' : 'text-green-600'}`}></i>
          )}
        </button>
      </div>

      {/* WhatsApp Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <a
          href="https://wa.me/919649408405"
          target="_blank"
          rel="noopener noreferrer"
          className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center shadow-2xl hover:bg-green-600 transition-all transform hover:scale-110 cursor-pointer"
          aria-label="Chat on WhatsApp"
        >
          <i className="ri-whatsapp-line text-3xl text-white"></i>
        </a>
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full animate-ping"></span>
      </div>
    </>
  );
}
